import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getNativeClientHeaders } from '../config/apiConfig';
import { isBookingAdminHandoffActive } from '../utils/bookingAdminHandoff';
import { shouldRedirectToLoginOnSessionExpiry } from '../utils/publicSessionRoutes';
import { identifyBookingUser, trackBookingLogin, trackBookingLogout } from '../analytics';
import { type AuthStatus, getInitialAuthStatus, isTokenValid } from '../utils/authStatus';
import {
  getAuthToken,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
  safeSessionStorageSetItem,
} from '../utils/safeStorage';

interface User {
  id: number;
  user_id: number; // Si nécessaire
  email: string;
  firstName: string;
  lastName: string;
  profile_photo_url: string;
  is_beta_tester?: boolean;
}

interface AuthContextType {
  user: User;
  login: (token: string) => void;
  logout: (options?: { redirect?: boolean }) => void;
  isAuthenticated: boolean;
  /** unknown = token présent, profil en cours ; guest = visiteur ; authenticated = session prête */
  authStatus: AuthStatus;
  /** true dès que authStatus n'est plus `unknown` (guest ou authenticated) */
  isAuthReady: boolean;
  /** true pendant la validation initiale du token / get-profile */
  isAuthLoading: boolean;
  forceUpdate: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(() =>
    getInitialAuthStatus(getAuthToken())
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [, setUpdate] = useState(0);
  const navigate = useNavigate();
  const pendingLoginTrackRef = useRef(false);
  const profileFetchGenerationRef = useRef(0);

  const logout = useCallback(
    (options?: { redirect?: boolean }) => {
      trackBookingLogout();
      void import('../services/appBadgeService')
        .then(({ clearAppBadgeCount }) => clearAppBadgeCount())
        .catch(() => {});
      safeLocalStorageRemoveItem('token');
      setUser(null);
      setIsAuthenticated(false);
      setAuthStatus('guest');
      setUpdate(prev => prev + 1);
      const redirect = options?.redirect ?? true;
      if (redirect) {
        navigate('/login');
      }
    },
    [navigate]
  );

  const fetchProfile = useCallback(async () => {
    if (isBookingAdminHandoffActive()) {
      return;
    }
    const fetchGeneration = ++profileFetchGenerationRef.current;
    const token = getAuthToken();
    if (token) {
      try {
        const response = await fetch(buildApiUrl('/get-profile'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...getNativeClientHeaders(),
          },
        });

        const raw = await response.text();
        let data: Record<string, unknown> = {};
        try {
          data = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        } catch {
          data = {};
        }

        if (isBookingAdminHandoffActive()) {
          return;
        }

        if (fetchGeneration !== profileFetchGenerationRef.current) {
          return;
        }

        if (response.ok) {
          setUser(data);
          setIsAuthenticated(true);
          setAuthStatus('authenticated');
          identifyBookingUser(data as unknown as User);
          if (pendingLoginTrackRef.current) {
            trackBookingLogin(data as unknown as User);
            pendingLoginTrackRef.current = false;
          }
        } else {
          if (fetchGeneration !== profileFetchGenerationRef.current) {
            return;
          }
          const msg =
            typeof data.Message === 'string'
              ? data.Message
              : typeof data.message === 'string'
                ? data.message
                : '';
          const expired =
            data.error_code === 'TOKEN_EXPIRED' ||
            msg.toLowerCase().includes('expir') ||
            msg.toLowerCase().includes('expired');
          if (response.status === 401 && expired) {
            safeSessionStorageSetItem('auth_notice', 'session_expired');
          }
          logout({ redirect: shouldRedirectToLoginOnSessionExpiry(window.location.pathname) });
        }
      } catch (error) {
        if (fetchGeneration !== profileFetchGenerationRef.current) {
          return;
        }
        console.error('Error fetching profile:', error);
        logout({ redirect: shouldRedirectToLoginOnSessionExpiry(window.location.pathname) });
      }
    }
  }, [logout]);

  useEffect(() => {
    if (isBookingAdminHandoffActive()) {
      return;
    }

    const token = getAuthToken();

    if (isTokenValid(token)) {
      setAuthStatus('unknown');
      fetchProfile();
    } else if (token) {
      logout({ redirect: shouldRedirectToLoginOnSessionExpiry(window.location.pathname) });
    } else {
      setAuthStatus('guest');
    }
  }, [fetchProfile, logout]);

  const login = (token: string) => {
    safeLocalStorageSetItem('token', token);
    pendingLoginTrackRef.current = true;
    setAuthStatus('unknown');
    setIsAuthenticated(true);
    fetchProfile();
    setUpdate(prev => prev + 1);
  };

  const forceUpdate = () => {
    setUpdate(prev => prev + 1);
  };

  const isAuthReady = authStatus !== 'unknown';
  const isAuthLoading = authStatus === 'unknown';

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        authStatus,
        isAuthReady,
        isAuthLoading,
        forceUpdate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Hors AuthProvider (tests isolés, previews) : undefined → traiter comme invité. */
export const useAuthOptional = (): AuthContextType | undefined => useContext(AuthContext);
